import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  // useTheme,
  Tab,
  Tabs
  // Tooltip,
  // Avatar,
  // Stack
} from '@mui/material';
import Favicon from 'components/Favicon';

const PositioningMatrices = () => {
  // const theme = useTheme();

  // Get data from Redux
  const sbsData = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults?.side_by_side_analysis);

  // Access the analysis object
  const analysis = sbsData?.analysis || {};

  // Get positioning matrix data
  const priceFeatureMatrix = analysis['7_multi_dimensional_comparison_matrices']?.positioning_matrix || {};
  const trustUserBaseMatrix = analysis['7_multi_dimensional_comparison_matrices']?.trust_vs_user_base_2x2 || {};

  // Get heatmap scorecard data for creating custom matrices
  const heatmapData = analysis['7_multi_dimensional_comparison_matrices']?.A_heatmap_scorecard || [];
  // console.log(heatmapData);

  // Get all companies from company scoring data
  const allCompanies = Object.keys(analysis['5_company_scoring'] || {});

  // Get all parameters from the heatmap data (excluding company and total)
  const parameters = heatmapData.length > 0 ? Object.keys(heatmapData[0]).filter((key) => key !== 'company' && key !== 'total') : [];

  // Generate all parameter combinations for 2x2 matrices
  const parameterCombinations = [];
  for (let i = 0; i < parameters.length; i++) {
    for (let j = i + 1; j < parameters.length; j++) {
      parameterCombinations.push({
        x: parameters[i],
        y: parameters[j]
      });
    }
  }

  // Pre-filter parameter combinations to those with at least 3 filled quadrants
  const filteredParameterCombos = parameterCombinations.filter((combo) => {
    // Helper to determine quadrant for a company
    const getQuadrant = (xValue, yValue) => {
      // Using 2.5 as midpoint for 1-5 scale
      const isHighX = xValue >= 2.5;
      const isHighY = yValue >= 2.5;

      if (isHighX && isHighY) return 'top-right';
      if (isHighX && !isHighY) return 'bottom-right';
      if (!isHighX && isHighY) return 'top-left';
      return 'bottom-left';
    };

    // Group companies by quadrant
    const quadrants = {
      'top-right': [],
      'top-left': [],
      'bottom-right': [],
      'bottom-left': []
    };

    // Assign companies to quadrants
    allCompanies.forEach((company) => {
      if (!analysis['5_company_scoring'][company]) return;

      const companyData = heatmapData.find((item) => item.company === company);
      if (!companyData) return;

      const xValue = companyData[combo.x] || 0;
      const yValue = companyData[combo.y] || 0;
      const quadrant = getQuadrant(xValue, yValue);

      quadrants[quadrant].push(company);
    });

    // Check if at least 3 quadrants are filled
    const nonEmptyQuadrants = Object.keys(quadrants).filter((q) => quadrants[q].length > 0);
    return nonEmptyQuadrants.length >= 3;
  });

  // Get unique parameters that have at least one matrix after filtering
  const uniqueParameters = [...new Set(filteredParameterCombos.flatMap((combo) => [combo.x, combo.y]))];

  // Tab state for controlling which parameter group to display
  const [currentParameterTab, setCurrentParameterTab] = useState(uniqueParameters[0] || '');

  // Update current tab if it's no longer valid after filtering
  React.useEffect(() => {
    if (uniqueParameters.length > 0 && !uniqueParameters.includes(currentParameterTab)) {
      setCurrentParameterTab(uniqueParameters[0]);
    }
  }, [currentParameterTab, uniqueParameters]);

  // State to track which companies are displayed
  const [selectedCompanies, setSelectedCompanies] = useState(allCompanies.reduce((acc, company) => ({ ...acc, [company]: true }), {}));

  // Handle checkbox change for company selection
  const handleCompanyToggle = (company) => {
    setSelectedCompanies({
      ...selectedCompanies,
      [company]: !selectedCompanies[company]
    });
  };

  // Handle tab change
  const handleParameterTabChange = (event, newValue) => {
    setCurrentParameterTab(newValue);
  };

  // Get color for company dot
  const getCompanyColor = (company) => {
    const colors = [
      '#673AB7', // Deep Purple
      '#455A64', // Dark Blue Grey (only grey in palette)
      '#1976D2', // Strong Blue
      '#0097A7', // Teal
      '#6A1B9A', // Dark Purple
      '#1A237E', // Navy Blue
      '#00695C', // Dark Teal
      '#3949AB', // Royal Blue
      '#4527A0', // Violet
      '#006064', // Dark Cyan
      '#5E35B1', // Bright Purple
      '#4A148C', // Deep Purple
      '#0D47A1', // Dark Blue
      '#26418F', // Medium Blue
      '#880E4F' // Deep Magenta
    ];

    const index = allCompanies.indexOf(company);
    return colors[index % colors.length];
  };

  // Format parameter name for display
  const formatParameterName = (param) => {
    return param
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format quadrant labels
  const formatQuadrantLabel = (quadrant) => {
    switch (quadrant) {
      case 'low_trust_small_base':
        return 'Low Trust, Small User Base';
      case 'low_trust_large_base':
        return 'Low Trust, Large User Base';
      case 'high_trust_small_base':
        return 'High Trust, Small User Base';
      case 'high_trust_large_base':
        return 'High Trust, Large User Base';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            Positioning Matrices
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            2x2 matrices showing competitive positioning across different dimensions
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Company Selection Checkboxes */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
            <FormGroup row>
              {allCompanies.map((company) => (
                <FormControlLabel
                  key={company}
                  control={
                    <Checkbox
                      checked={selectedCompanies[company]}
                      onChange={() => handleCompanyToggle(company)}
                      name={company}
                      sx={{
                        color: getCompanyColor(company),
                        '&.Mui-checked': {
                          color: getCompanyColor(company)
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Favicon company={company} size={16} />
                      {company}
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Box>

          {/* Default Matrices */}
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            Key Positioning Matrices
          </Typography>

          <Grid container spacing={4} sx={{ mb: 4 }}>
            {/* Price vs Feature Set Matrix */}
            {(() => {
              // Check if we have the required data
              if (!priceFeatureMatrix?.matrix || !Array.isArray(priceFeatureMatrix.matrix) || priceFeatureMatrix.matrix.length === 0) {
                return null;
              }

              // Count how many quadrants have companies in the Price vs Feature Set matrix
              const priceFeatureQuadrants = {
                'top-right': priceFeatureMatrix.matrix.filter(
                  (item) =>
                    selectedCompanies[item.company] &&
                    item.price >= 3 &&
                    item.feature_set >= (priceFeatureMatrix.feature_max + priceFeatureMatrix.feature_min) / 2
                ),
                'top-left': priceFeatureMatrix.matrix.filter(
                  (item) =>
                    selectedCompanies[item.company] &&
                    item.price < 3 &&
                    item.feature_set >= (priceFeatureMatrix.feature_max + priceFeatureMatrix.feature_min) / 2
                ),
                'bottom-right': priceFeatureMatrix.matrix.filter(
                  (item) =>
                    selectedCompanies[item.company] &&
                    item.price >= 3 &&
                    item.feature_set < (priceFeatureMatrix.feature_max + priceFeatureMatrix.feature_min) / 2
                ),
                'bottom-left': priceFeatureMatrix.matrix.filter(
                  (item) =>
                    selectedCompanies[item.company] &&
                    item.price < 3 &&
                    item.feature_set < (priceFeatureMatrix.feature_max + priceFeatureMatrix.feature_min) / 2
                )
              };

              // Count companies in the matrix
              const totalSelectedCompanies = priceFeatureMatrix.matrix.filter((item) => selectedCompanies[item.company]).length;

              // Count non-empty quadrants
              const nonEmptyQuadrants = Object.values(priceFeatureQuadrants).filter((quadrant) => quadrant.length > 0).length;

              // Skip rendering if all companies are in the same quadrant
              if (nonEmptyQuadrants <= 1 && totalSelectedCompanies > 0) {
                return null;
              }

              return (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Price vs Feature Set
                  </Typography>
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: 'auto',
                        p: 2,
                        pb: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        mb: 4,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gridTemplateRows: 'auto auto',
                          gap: 2,
                          height: 'auto',
                          position: 'relative'
                        }}
                      >
                        {/* Top Left Quadrant - High Feature Set, Low Accessibility */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible',
                            bgcolor: '#edf7ed'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            High Feature Set, Low Accessibility
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {priceFeatureQuadrants['top-left'].map((item) => (
                              <Box
                                key={item.company}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  pl: 1.5,
                                  pr: 4,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderLeft: '3px solid',
                                  borderLeftColor: getCompanyColor(item.company),
                                  bgcolor: 'background.paper',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                    transform: 'translateY(-2px)'
                                  },
                                  display: 'inline-block',
                                  maxWidth: 'fit-content',
                                  mr: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Favicon company={item.company} size={20} />
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ color: getCompanyColor(item.company), mb: 0.5 }}>
                                      {item.company}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Price: {item.price.toFixed(1)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Feature: {item.feature_set.toFixed(1)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                            {priceFeatureQuadrants['top-left'].length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Top Right Quadrant - High Feature Set, High Accessibility */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible',
                            bgcolor: '#f8fbf8'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            High Feature Set, High Accessibility
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {priceFeatureQuadrants['top-right'].map((item) => (
                              <Box
                                key={item.company}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  pl: 1.5,
                                  pr: 4,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderLeft: '3px solid',
                                  borderLeftColor: getCompanyColor(item.company),
                                  bgcolor: 'background.paper',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                    transform: 'translateY(-2px)'
                                  },
                                  display: 'inline-block',
                                  maxWidth: 'fit-content',
                                  mr: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Favicon company={item.company} size={20} />
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ color: getCompanyColor(item.company), mb: 0.5 }}>
                                      {item.company}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Price: {item.price.toFixed(1)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Feature: {item.feature_set.toFixed(1)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                            {priceFeatureQuadrants['top-right'].length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Bottom Left Quadrant - Low Feature Set, Low Accessibility */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            Low Feature Set, Low Accessibility
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {priceFeatureQuadrants['bottom-left'].map((item) => (
                              <Box
                                key={item.company}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  pl: 1.5,
                                  pr: 4,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderLeft: '3px solid',
                                  borderLeftColor: getCompanyColor(item.company),
                                  bgcolor: 'background.paper',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                    transform: 'translateY(-2px)'
                                  },
                                  display: 'inline-block',
                                  maxWidth: 'fit-content',
                                  mr: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Favicon company={item.company} size={20} />
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ color: getCompanyColor(item.company), mb: 0.5 }}>
                                      {item.company}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Price: {item.price.toFixed(1)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Feature: {item.feature_set.toFixed(1)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                            {priceFeatureQuadrants['bottom-left'].length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Bottom Right Quadrant - Low Feature Set, High Accessibility */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible',
                            bgcolor: '#f8fbf8'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            Low Feature Set, High Accessibility
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {priceFeatureQuadrants['bottom-right'].map((item) => (
                              <Box
                                key={item.company}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  pl: 1.5,
                                  pr: 4,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderLeft: '3px solid',
                                  borderLeftColor: getCompanyColor(item.company),
                                  bgcolor: 'background.paper',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                    transform: 'translateY(-2px)'
                                  },
                                  display: 'inline-block',
                                  maxWidth: 'fit-content',
                                  mr: 1
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Favicon company={item.company} size={20} />
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ color: getCompanyColor(item.company), mb: 0.5 }}>
                                      {item.company}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Price: {item.price.toFixed(1)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      Feature: {item.feature_set.toFixed(1)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                            {priceFeatureQuadrants['bottom-right'].length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 'medium',
                          bgcolor: 'background.paper',
                          px: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}
                      >
                        Price Accessibility (Low → High)
                      </Typography>
                    </Box>

                    {/* Y-Axis Label */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: -5,
                        transform: 'rotate(-90deg) translateX(-50%)',
                        transformOrigin: 'left center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 'medium',
                          bgcolor: 'background.paper',
                          px: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}
                      >
                        Feature Set (Low → High)
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })()}

            {/* Trust vs User Base Matrix */}
            {(() => {
              // Validate data structure
              if (
                !trustUserBaseMatrix ||
                typeof trustUserBaseMatrix !== 'object' ||
                !Array.isArray(trustUserBaseMatrix.high_trust_small_base) ||
                !Array.isArray(trustUserBaseMatrix.high_trust_large_base) ||
                !Array.isArray(trustUserBaseMatrix.low_trust_small_base) ||
                !Array.isArray(trustUserBaseMatrix.low_trust_large_base)
              ) {
                return null;
              }

              // Count companies in each quadrant of the Trust vs User Base matrix
              const trustBaseQuadrants = {
                high_trust_small_base: trustUserBaseMatrix.high_trust_small_base.filter((company) => selectedCompanies[company]),
                high_trust_large_base: trustUserBaseMatrix.high_trust_large_base.filter((company) => selectedCompanies[company]),
                low_trust_small_base: trustUserBaseMatrix.low_trust_small_base.filter((company) => selectedCompanies[company]),
                low_trust_large_base: trustUserBaseMatrix.low_trust_large_base.filter((company) => selectedCompanies[company])
              };

              // Total companies shown in this matrix
              const totalDisplayedCompanies = Object.values(trustBaseQuadrants).reduce((total, companies) => total + companies.length, 0);

              // Count non-empty quadrants
              const nonEmptyQuadrants = Object.values(trustBaseQuadrants).filter((companies) => companies.length > 0).length;

              // Skip rendering if all companies are in the same quadrant
              if (nonEmptyQuadrants <= 1 && totalDisplayedCompanies > 0) {
                return null;
              }

              return (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Trust vs User Base
                  </Typography>
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: 'auto',
                        p: 2,
                        pb: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '8px',
                        mb: 4,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gridTemplateRows: 'auto auto',
                          gap: 2,
                          height: 'auto',
                          position: 'relative'
                        }}
                      >
                        {/* Top Left Quadrant - High Trust, Small User Base */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible',
                            bgcolor: '#f8fbf8' // Extremely light green background
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            {formatQuadrantLabel('high_trust_small_base')}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {trustUserBaseMatrix.high_trust_small_base
                              .filter((company) => selectedCompanies[company])
                              .map((company) => (
                                <Box
                                  key={company}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    pl: 1.5,
                                    pr: 4,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderLeft: '3px solid',
                                    borderLeftColor: getCompanyColor(company),
                                    bgcolor: 'background.paper',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                      transform: 'translateY(-2px)'
                                    },
                                    display: 'inline-block',
                                    maxWidth: 'fit-content',
                                    mr: 1
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Favicon company={company} size={20} />
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: getCompanyColor(company), mb: 0.5 }}>
                                        {company}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        High Trust, Small User Base
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            {trustUserBaseMatrix.high_trust_small_base.filter((company) => selectedCompanies[company]).length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Top Right Quadrant - High Trust, Large User Base */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible',
                            bgcolor: '#edf7ed' // Light but slightly more saturated green
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            {formatQuadrantLabel('high_trust_large_base')}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {trustUserBaseMatrix.high_trust_large_base
                              .filter((company) => selectedCompanies[company])
                              .map((company) => (
                                <Box
                                  key={company}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    pl: 1.5,
                                    pr: 4,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderLeft: '3px solid',
                                    borderLeftColor: getCompanyColor(company),
                                    bgcolor: 'background.paper',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                      transform: 'translateY(-2px)'
                                    },
                                    display: 'inline-block',
                                    maxWidth: 'fit-content',
                                    mr: 1
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Favicon company={company} size={20} />
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: getCompanyColor(company), mb: 0.5 }}>
                                        {company}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        High Trust, Large User Base
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            {trustUserBaseMatrix.high_trust_large_base.filter((company) => selectedCompanies[company]).length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Bottom Left Quadrant - Low Trust, Small User Base */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible'
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            {formatQuadrantLabel('low_trust_small_base')}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {trustUserBaseMatrix.low_trust_small_base
                              .filter((company) => selectedCompanies[company])
                              .map((company) => (
                                <Box
                                  key={company}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    pl: 1.5,
                                    pr: 4,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderLeft: '3px solid',
                                    borderLeftColor: getCompanyColor(company),
                                    bgcolor: 'background.paper',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                      transform: 'translateY(-2px)'
                                    },
                                    display: 'inline-block',
                                    maxWidth: 'fit-content',
                                    mr: 1
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Favicon company={company} size={20} />
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: getCompanyColor(company), mb: 0.5 }}>
                                        {company}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        Low Trust, Small User Base
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            {trustUserBaseMatrix.low_trust_small_base.filter((company) => selectedCompanies[company]).length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Bottom Right Quadrant - Low Trust, Large User Base */}
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            overflow: 'visible',
                            bgcolor: '#f8fbf8' // Extremely light green background
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            align="center"
                            sx={{
                              fontSize: '0.75rem',
                              mb: 1.5,
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.primary',
                              bgcolor: 'background.paper',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              position: 'relative',
                              top: 0,
                              zIndex: 1,
                              width: '100%'
                            }}
                          >
                            {formatQuadrantLabel('low_trust_large_base')}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            {trustUserBaseMatrix.low_trust_large_base
                              .filter((company) => selectedCompanies[company])
                              .map((company) => (
                                <Box
                                  key={company}
                                  sx={{
                                    mb: 1,
                                    p: 1,
                                    pl: 1.5,
                                    pr: 4,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderLeft: '3px solid',
                                    borderLeftColor: getCompanyColor(company),
                                    bgcolor: 'background.paper',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                      transform: 'translateY(-2px)'
                                    },
                                    display: 'inline-block',
                                    maxWidth: 'fit-content',
                                    mr: 1
                                  }}
                                >
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Favicon company={company} size={20} />
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ color: getCompanyColor(company), mb: 0.5 }}>
                                        {company}
                                      </Typography>
                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        Low Trust, Large User Base
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            {trustUserBaseMatrix.low_trust_large_base.filter((company) => selectedCompanies[company]).length === 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                  No companies in this quadrant
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        textAlign: 'center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 'medium',
                          bgcolor: 'background.paper',
                          px: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}
                      >
                        Trust (Low → High)
                      </Typography>
                    </Box>

                    {/* Y-Axis Label */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: -5,
                        transform: 'rotate(-90deg) translateX(-50%)',
                        transformOrigin: 'left center'
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 'medium',
                          bgcolor: 'background.paper',
                          px: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1
                        }}
                      >
                        User Base (Small → Large)
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })()}
          </Grid>

          {/* Parameter Combination Matrices */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
            Parameter Combination Matrices
          </Typography>

          {/* Display parameter tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={currentParameterTab} onChange={handleParameterTabChange} variant="scrollable" scrollButtons="auto">
              {uniqueParameters.map((param) => (
                <Tab key={param} value={param} label={formatParameterName(param)} />
              ))}
            </Tabs>
          </Box>

          {/* Display matrices for the selected parameter */}
          <Grid container spacing={4}>
            {filteredParameterCombos
              // Filter combinations that include the selected parameter
              .filter((combo) => combo.x === currentParameterTab || combo.y === currentParameterTab)
              .map((combo) => {
                // Helper to determine quadrant for a company
                const getQuadrant = (xValue, yValue) => {
                  // Using 2.5 as midpoint for 1-5 scale
                  const isHighX = xValue >= 2.5;
                  const isHighY = yValue >= 2.5;

                  if (isHighX && isHighY) return 'top-right';
                  if (isHighX && !isHighY) return 'bottom-right';
                  if (!isHighX && isHighY) return 'top-left';
                  return 'bottom-left';
                };

                // Group companies by quadrant
                const quadrants = {
                  'top-right': [],
                  'top-left': [],
                  'bottom-right': [],
                  'bottom-left': []
                };

                // Assign companies to quadrants
                const filteredCompanies = allCompanies.filter((company) => selectedCompanies[company]);

                filteredCompanies.forEach((company) => {
                  const companyData = heatmapData.find((item) => item.company === company);
                  if (!companyData) return;

                  const xValue = companyData[combo.x] || 0;
                  const yValue = companyData[combo.y] || 0;
                  const quadrant = getQuadrant(xValue, yValue);

                  quadrants[quadrant].push({
                    company,
                    xValue,
                    yValue,
                    color: getCompanyColor(company)
                  });
                });

                // Check if all companies are in the same quadrant
                const nonEmptyQuadrants = Object.keys(quadrants).filter((q) => quadrants[q].length > 0);
                if (nonEmptyQuadrants.length < 3 && filteredCompanies.length > 0) {
                  // Skip rendering this matrix if fewer than 3 quadrants are filled
                  return null;
                }

                return (
                  <Grid item xs={12} key={`matrix-${combo.x}-${combo.y}`}>
                    <Typography variant="h6" gutterBottom>
                      {formatParameterName(combo.x)} vs {formatParameterName(combo.y)}
                    </Typography>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          height: 'auto',
                          p: 2,
                          pb: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          borderRadius: '8px',
                          mb: 4,
                          position: 'relative'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gridTemplateRows: 'auto auto',
                            gap: 2,
                            height: 'auto',
                            position: 'relative'
                          }}
                        >
                          {/* Top Left Quadrant - High Feature Set, Low Accessibility */}
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5,
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              overflow: 'visible',
                              bgcolor: '#edf7ed'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              align="center"
                              sx={{
                                fontSize: '0.75rem',
                                mb: 1.5,
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                                top: 0,
                                zIndex: 1,
                                width: '100%'
                              }}
                            >
                              High Feature Set, Low Accessibility
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              {quadrants['top-left'].length > 0 ? (
                                quadrants['top-left'].map((item) => (
                                  <Box
                                    key={item.company}
                                    sx={{
                                      mb: 1,
                                      p: 1,
                                      pl: 1.5,
                                      pr: 4,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderLeft: '3px solid',
                                      borderLeftColor: item.color,
                                      bgcolor: 'background.paper',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-2px)'
                                      },
                                      display: 'inline-block',
                                      maxWidth: 'fit-content',
                                      mr: 1
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Favicon company={item.company} size={20} />
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: item.color, mb: 0.5 }}>
                                          {item.company}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.x)}: {item.xValue.toFixed(1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.y)}: {item.yValue.toFixed(1)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}
                                >
                                  <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                    No companies in this quadrant
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Top Right Quadrant - High Feature Set, High Accessibility */}
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5,
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              overflow: 'visible',
                              bgcolor: '#f8fbf8'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              align="center"
                              sx={{
                                fontSize: '0.75rem',
                                mb: 1.5,
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                                top: 0,
                                zIndex: 1,
                                width: '100%'
                              }}
                            >
                              High Feature Set, High Accessibility
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              {quadrants['top-right'].length > 0 ? (
                                quadrants['top-right'].map((item) => (
                                  <Box
                                    key={item.company}
                                    sx={{
                                      mb: 1,
                                      p: 1,
                                      pl: 1.5,
                                      pr: 4,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderLeft: '3px solid',
                                      borderLeftColor: item.color,
                                      bgcolor: 'background.paper',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-2px)'
                                      },
                                      display: 'inline-block',
                                      maxWidth: 'fit-content',
                                      mr: 1
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Favicon company={item.company} size={20} />
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: item.color, mb: 0.5 }}>
                                          {item.company}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.x)}: {item.xValue.toFixed(1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.y)}: {item.yValue.toFixed(1)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}
                                >
                                  <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                    No companies in this quadrant
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Bottom Left Quadrant - Low Feature Set, Low Accessibility */}
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5,
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              overflow: 'visible'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              align="center"
                              sx={{
                                fontSize: '0.75rem',
                                mb: 1.5,
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                                top: 0,
                                zIndex: 1,
                                width: '100%'
                              }}
                            >
                              Low Feature Set, Low Accessibility
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              {quadrants['bottom-left'].length > 0 ? (
                                quadrants['bottom-left'].map((item) => (
                                  <Box
                                    key={item.company}
                                    sx={{
                                      mb: 1,
                                      p: 1,
                                      pl: 1.5,
                                      pr: 4,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderLeft: '3px solid',
                                      borderLeftColor: item.color,
                                      bgcolor: 'background.paper',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-2px)'
                                      },
                                      display: 'inline-block',
                                      maxWidth: 'fit-content',
                                      mr: 1
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Favicon company={item.company} size={20} />
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: item.color, mb: 0.5 }}>
                                          {item.company}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.x)}: {item.xValue.toFixed(1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.y)}: {item.yValue.toFixed(1)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}
                                >
                                  <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                    No companies in this quadrant
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>

                          {/* Bottom Right Quadrant - Low Feature Set, High Accessibility */}
                          <Box
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5,
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              overflow: 'visible',
                              bgcolor: '#f8fbf8'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              align="center"
                              sx={{
                                fontSize: '0.75rem',
                                mb: 1.5,
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.primary',
                                bgcolor: 'background.paper',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                position: 'relative',
                                top: 0,
                                zIndex: 1,
                                width: '100%'
                              }}
                            >
                              Low Feature Set, High Accessibility
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              {quadrants['bottom-right'].length > 0 ? (
                                quadrants['bottom-right'].map((item) => (
                                  <Box
                                    key={item.company}
                                    sx={{
                                      mb: 1,
                                      p: 1,
                                      pl: 1.5,
                                      pr: 4,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderLeft: '3px solid',
                                      borderLeftColor: item.color,
                                      bgcolor: 'background.paper',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-2px)'
                                      },
                                      display: 'inline-block',
                                      maxWidth: 'fit-content',
                                      mr: 1
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Favicon company={item.company} size={20} />
                                      <Box>
                                        <Typography variant="subtitle2" sx={{ color: item.color, mb: 0.5 }}>
                                          {item.company}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.x)}: {item.xValue.toFixed(1)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          {formatParameterName(combo.y)}: {item.yValue.toFixed(1)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ))
                              ) : (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}
                                >
                                  <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                                    No companies in this quadrant
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          textAlign: 'center'
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontWeight: 'medium',
                            bgcolor: 'background.paper',
                            px: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          {formatParameterName(combo.x)} (Low → High)
                        </Typography>
                      </Box>

                      {/* Y-Axis Label */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: -5,
                          transform: 'rotate(-90deg) translateX(-50%)',
                          transformOrigin: 'left center'
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontWeight: 'medium',
                            bgcolor: 'background.paper',
                            px: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          {formatParameterName(combo.y)} (Low → High)
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PositioningMatrices;
